import type { ServiceType } from '../types/agreement'
import {
  mapRequestProjectTypeToServiceType,
  type ProposalRequestRow,
} from './mapProposalRequestToProposal'
import type { ServiceCategoryOption } from './workTypeMapping'

/** Canonical service type dropdown — shared by proposal requests and proposals. */
export const SERVICE_TYPE_OPTIONS: Array<{ value: ServiceType; label: string }> = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'maintenance_dsny', label: 'Maintenance DSNY' },
  { value: 'maintenance_prevailing_wage', label: 'Prevailing Wage' },
  { value: 'dsny_prevailing_wage', label: 'Prevailing Wage DSNY' },
  { value: 'construction_cleaning', label: 'Construction Cleaning' },
  { value: 'apartment_turnovers', label: 'Apartment Turnovers' },
  { value: 'office_cleaning', label: 'Office Cleaning' },
  { value: 'hourly_clean', label: 'Regular Hourly Cleaning' },
  { value: 'custom', label: 'Custom' },
]

/**
 * Legacy service-type labels kept so old records still display a friendly name
 * even though these options are no longer offered in the dropdown.
 */
const LEGACY_SERVICE_TYPE_LABELS: Record<string, string> = {
  cleaning: 'Cleaning',
  landscaping: 'Landscaping',
  security: 'Security',
  dsny: 'DSNY',
}

/** Service types that use DSNY pickup scheduling and integration. */
export function isDsnyServiceType(value: string | null | undefined): boolean {
  if (!value?.trim()) return false
  return (
    value === 'maintenance_dsny' ||
    value === 'dsny' ||
    value === 'dsny_prevailing_wage'
  )
}

/** Service types treated as maintenance-family (recurring building upkeep). */
export function isMaintenanceFamilyServiceType(value: string | null | undefined): boolean {
  if (!value?.trim()) return false
  return (
    value === 'maintenance' ||
    value === 'maintenance_dsny' ||
    value === 'maintenance_prevailing_wage' ||
    value === 'dsny_prevailing_wage'
  )
}

export function getServiceTypeLabel(value: string | null | undefined): string {
  if (!value?.trim()) return ''
  const match = SERVICE_TYPE_OPTIONS.find((o) => o.value === value)
  if (match) return match.label
  if (LEGACY_SERVICE_TYPE_LABELS[value]) return LEGACY_SERVICE_TYPE_LABELS[value]
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Service types billed/presented as a flat project, hourly, or per-unit job (not recurring upkeep). */
export function isProjectCleaningServiceType(value: string | null | undefined): boolean {
  if (!value?.trim()) return false
  return (
    value === 'construction_cleaning' ||
    value === 'apartment_turnovers' ||
    value === 'office_cleaning' ||
    value === 'hourly_clean' ||
    value === 'cleaning'
  )
}

/** Service types that are prevailing-wage variants (customer furnishes supplies/equipment by default). */
export function isPrevailingWageServiceType(value: string | null | undefined): boolean {
  if (!value?.trim()) return false
  return value === 'maintenance_prevailing_wage' || value === 'dsny_prevailing_wage'
}

/** Auto proposal title: street address + service type label (e.g. "89 5th Ave - Cleaning"). */
export function buildAutoProposalTitle(
  address: string | null | undefined,
  serviceType: string | null | undefined
): string {
  const street = address?.split(',')[0]?.trim() || ''
  const label = getServiceTypeLabel(serviceType)
  if (street && label) return `${street} - ${label}`
  return street || label || ''
}

/** Resolve service_type from request row (prefers column, maps legacy project_type). */
export function resolveRequestServiceType(
  request: Pick<ProposalRequestRow, 'service_type' | 'project_type'> | null | undefined,
  serviceCategories: ServiceCategoryOption[] = []
): string {
  if (!request) return ''
  if (request.service_type?.trim()) return request.service_type.trim()
  return mapRequestProjectTypeToServiceType(request.project_type, serviceCategories)
}
